import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, setTokens, clearTokens, getToken } from "@/lib/api";
import { applyUserPreferences, initPreferences } from "@/lib/preferences";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, object=auth
  const [tenant, setTenant] = useState(null); // current tenant branding info
  const [error, setError] = useState(null);

  // Apply cached prefs immediately on mount to avoid theme flash
  useEffect(() => { initPreferences(); }, []);

  const fetchTenant = useCallback(async () => {
    try {
      const { data } = await api.get("/tenants/current");
      setTenant(data);
    } catch (_) {
      setTenant(null);
    }
  }, []);

  const fetchMe = useCallback(async () => {
    if (!getToken()) {
      setUser(false);
      setTenant(null);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Hydrate preferences from server (overrides localStorage)
      try {
        const { data: full } = await api.get("/users/me");
        if (full?.preferences) {
          applyUserPreferences(full.preferences);
        }
      } catch (_) { /* portal users may not have /users/me access */ }
      await fetchTenant();
    } catch (_) {
      clearTokens();
      setUser(false);
      setTenant(null);
    }
  }, [fetchTenant]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setTokens(data.access_token, data.refresh_token);
      const { access_token, refresh_token, ...userData } = data;
      setUser(userData);
      await fetchTenant();
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) { /* ignore */ }
    clearTokens();
    setUser(false);
    setTenant(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, tenant, login, logout, error, setError, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
